import { useState, useEffect } from "react";
import MFOCalendarView from "@/components/calendar/MFOCalendarView";
import MFODetailDialog from "@/components/mfo/MFODetailDialog";
import { MFOItem } from "@/types/kanban";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const CalendarPage = () => {
  const [selectedMFO, setSelectedMFO] = useState<MFOItem | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(true);
  const [mfoItems, setMfoItems] = useState<MFOItem[]>([]);

  // Direct data fetching on component mount
  useEffect(() => {
    const fetchMFOData = async () => {
      setIsLoading(true);
      try {

        
        // Fetch MFO items directly from Supabase
        const { data: mfoItemsData, error: mfoItemsError } = await supabase
          .from('mfo_items')
          .select(`
            id, code, title, budget, priority, due_date, status, 
            progress, q1, q2, q3, q4, department_id,
            departments (id, name),
            mfo_success_indicators (indicator),
            mfo_assignees (
              employee:employees (id, name, avatar)
            )
          `);
          
        if (mfoItemsError) {
          throw mfoItemsError;
        }

        // Process the fetched data
        const processedData = mfoItemsData.map(item => {
          // Ensure due_date is properly parsed as a Date object
          let parsedDueDate = null;
          if (item.due_date) {
            // Handle both ISO string and date objects
            parsedDueDate = new Date(item.due_date);
            
            // Validate the date is valid
            if (isNaN(parsedDueDate.getTime())) {
              console.warn(`Invalid due date for MFO ${item.id}: ${item.due_date}`);
              parsedDueDate = null;
            }
          }
          
          return {
            id: item.id,
            code: item.code,
            title: item.title,
            budget: Number(item.budget),
            priority: item.priority as "high" | "medium" | "low",
            status: item.status as "not-started" | "in-progress" | "delayed" | "completed",
            // Ensure dueDate is a properly formatted date string that can be parsed by the calendar
            dueDate: parsedDueDate ? parsedDueDate.toISOString() : null,
            progress: item.progress || 0,
            departmentId: item.department_id,
            departmentName: item.departments?.name,
            timeline: {
              q1: item.q1,
              q2: item.q2,
              q3: item.q3,
              q4: item.q4
            },
            successIndicators: item.mfo_success_indicators?.map(si => si.indicator) || [],
            collaborators: item.mfo_assignees?.map(a => ({
              id: a.employee?.id,
              name: a.employee?.name,
              avatar: a.employee?.avatar
            })) || []
          };
        });

        // Store in query cache
        queryClient.setQueryData(['mfoItems'], processedData || []);
        
        // Update local state
        setMfoItems(processedData || []);
        
      } catch (error) {
        console.error("Error fetching MFO data for Calendar:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchMFOData();
  }, [queryClient]);

  const handleMFOClick = (mfo: MFOItem) => {
    setSelectedMFO(mfo);
    setIsDetailOpen(true);
  };

  return (
    <div className="space-y-8">
      <h1 className="text-3xl font-bold">Calendar</h1>
      
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-2 text-sm text-muted-foreground">Loading calendar data...</span>
        </div>
      )}
      
      {!isLoading && mfoItems.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No MFO items found with due dates to display on the calendar
        </div>
      ) : (
        <MFOCalendarView 
          onMFOClick={handleMFOClick} 
          mfoItems={mfoItems} 
        />
      )}
      
      {selectedMFO && (
        <MFODetailDialog
          isOpen={isDetailOpen}
          onOpenChange={setIsDetailOpen}
          mfo={selectedMFO}
        />
      )}
    </div>
  );
};

export default CalendarPage;